import { useMemo } from "react";
import { useFavoriteIssues } from "#app/lib/hooks/use-favorite-issues";
import type { IssueRow } from "#app/lib/data";
import type { EntityListSort } from "../entity-list/use-entity-list";

/**
 * The data layer for the `favorites-list` card: the viewer's starred issues.
 *
 * Favorites are user-starred **issues** (`useFavoriteIssues` → `myPreferences.favorites`),
 * NOT assignee-scoped — so this card deliberately bypasses the dashboard scope fold a
 * scoped card would apply (folding member scope in would show only favorited-AND-assigned
 * issues). The starred set arrives already as the issue row the shared `IssueTable`
 * columns render — the FULL set, bounded by what the viewer starred — so the only work
 * here is the client-side sort (the favorites query has no server ordering). Every
 * favorite shows — the table's windowed scroll pages the set, never a cap.
 */
export function useFavoritesList(sort: EntityListSort): { rows: IssueRow[]; loading: boolean } {
  const { issues, isLoading } = useFavoriteIssues();

  const rows = useMemo(
    () => [...issues].sort((a, b) => compareIssues(a, b, sort)),
    [issues, sort],
  );

  return { rows, loading: isLoading };
}

/** Client-side ordering mirroring `entity-list`'s server `orderBy`: newest/recently
 *  updated first (nulls last), or name A→Z. */
function compareIssues(a: IssueRow, b: IssueRow, sort: EntityListSort): number {
  if (sort === "name") return a.name.localeCompare(b.name);
  const field = sort === "updated" ? "lastUpdateDate" : "creationDate";
  return dateDesc(a[field], b[field]);
}

/** Descending ISO-date compare (newest first); missing dates sort last. */
function dateDesc(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return b.localeCompare(a);
}
